/*
CRITICAL API DATA FORMAT REQUIREMENTS

1. Data Format Rules:
   - ALL API endpoints expect data in snake_case format
   - Frontend components use camelCase internally
   - Data conversion happens in the components, NOT in this service
   - This service should receive and pass through snake_case data without modification

2. Field Naming Convention:
   Frontend (camelCase)     Backend (snake_case)
   ------------------     -------------------
   firstName             first_name
   lastName              last_name
   displayName           display_name
   companyName           company_name
   companyWebsite        company_website
   businessArea          business_area
   photoUrl             photo_url
   companyLogoUrl       company_logo_url
   companyBio           company_bio
   idealCustomer        ideal_customer
   avoidReferring       avoid_referring
   linkedinUrl          linkedin_url

3. Common Mistakes to Avoid:
   - DO NOT convert snake_case to camelCase in this service
   - DO NOT assume data format - always use the format as received
   - DO NOT modify field names - pass through exactly as received
   - ALWAYS include all fields in the data object

4. When Adding New Fields:
   - Add field to relevant interface/type definitions
   - Add field to all relevant API methods
   - Maintain snake_case format consistently
   - Document new fields in this header
*/

import axios from 'axios';

// API service initialized

export const getBaseUrl = () => process.env.VUE_APP_API_URL || '';

export const api = axios.create({
    baseURL: process.env.VUE_APP_API_URL || '',
    timeout: 10000
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle responses
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            console.error('[api.js] 401 Unauthorized error:', {
                url: error.config?.url,
                method: error.config?.method,
                hasAuthHeader: !!error.config?.headers?.Authorization,
                errorData: error.response?.data
            });
            
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        throw error;
    }
);

// Export services
export const userService = {
    getProfile: async () => {
        const response = await api.get('/users/profile');
        return response.data;
    },

    // IMPORTANT: This function receives data in snake_case format from UserProfile.vue
    // The frontend has already formatted the data correctly - DO NOT try to convert it
    updateProfile: async (userData) => {
        // Simply pass through the data as-is - it's already in the correct format
        const backendData = {
            email: userData.email,
            display_name: userData.display_name,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role,
            photo_url: userData.photo_url,
            title: userData.title,
            company_website: userData.company_website || null,
            company_name: userData.company_name,
            business_area: userData.business_area,
            phone: userData.phone,
            bio: userData.bio,
            partner_name: userData.partner_name,
            birth_day: userData.birth_day,
            birth_month: userData.birth_month,
            coffee_order_1: userData.coffee_order_1,
            coffee_order_2: userData.coffee_order_2,
            dietary_requirements: userData.dietary_requirements,
            company_logo_url: userData.company_logo_url,
            company_bio: userData.company_bio,
            ideal_customer: userData.ideal_customer,
            avoid_referring: userData.avoid_referring,
            linkedin_url: userData.linkedin_url
        };

        return api.put('/users/profile', backendData);
    },

    updateProfilePhoto: async (userId, photoUrl, isAdminMode = false) => {
        const response = await api.put('/users/profile-photo', {
            userId,
            photoUrl,
            isAdminMode
        });
        
        return response.data;
    },

    getAllUsers: async () => {
        const response = await api.get('/users/all');
        return response.data;
    },

    createUser: async (userData) => {
        // Convert camelCase to snake_case for backend
        const backendData = {
            email: userData.email,
            display_name: userData.displayName,
            first_name: userData.firstName,
            last_name: userData.lastName,
            title: userData.title,
            company_website: userData.companyWebsite || null,
            company_name: userData.companyName,
            business_area: userData.businessArea,
            phone: userData.phone,
            bio: userData.bio,
            partner_name: userData.partnerName,
            birth_date: userData.birthDate,
            coffee_order_1: userData.coffeeOrder1,
            coffee_order_2: userData.coffeeOrder2,
            dietary_requirements: userData.dietaryRequirements,
            company_logo_url: userData.companyLogoUrl,
            company_bio: userData.companyBio,
            ideal_customer: userData.idealCustomer,
            avoid_referring: userData.avoidReferring,
            linkedin_url: userData.linkedinUrl
        };
        
        const response = await api.post('/users', backendData);
        return response.data;
    },

    updateUser: async (userId, userData) => {
        const response = await api.put(`/users/${userId}`, userData);
        return response.data;
    },

    updateUserRole: async (userId, role) => {
        const response = await api.put(`/users/${userId}/role`, { role });
        return response.data;
    },

    deleteUser: async (userId) => {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
    },

    getUser: async (userId) => {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    },

    updateCompanyLogo: async (logoUrl, userId, isAdminMode = false) => {
        const response = await api.put('/users/company-logo', {
            logoUrl,
            userId,
            isAdminMode
        });
        return response.data;
    },

    getUpcomingBirthdays: async () => {
        const response = await api.get('/users/upcoming-birthdays');
        return response.data;
    },

    getActiveMembers() {
        return api.get('/users?status=active');
    },

    acceptTerms: async () => {
        const response = await api.post('/users/accept-terms');
        return response.data;
    },

    hasAcceptedTerms: async () => {
        const response = await api.get('/users/terms-status');
        return response.data.accepted;
    },

    getAllMembersWithPreferences: async () => {
        const response = await api.get('/users/members-preferences');
        return response.data;
    },

    updateMemberStatus: async (userId, status, notes) => {
        // Ensure status is a string and one of the allowed values
        const validStatus = String(status).toLowerCase();
        if (!['active', 'inactive'].includes(validStatus)) {
            throw new Error('Invalid status value');
        }
        
        const response = await api.put(`/users/${userId}/status`, { 
            status: validStatus,
            notes: notes || null 
        });
        return response.data;
    },

    sendPasswordReset: async (userId) => {
        const response = await api.post(`/users/${userId}/password-reset`);
        return response.data;
    },

    setUserPassword: async (userId, newPassword) => {
        const response = await api.post(`/users/${userId}/set-password`, {
            newPassword
        });
        return response.data;
    }
};

export const eventService = {
    getAllEvents: async () => {
        const response = await api.get('/events');
        return response.data;
    },

    createEvent: async (eventData) => {
        const response = await api.post('/events', eventData);
        return response.data;
    },

    updateEvent: async (eventId, eventData) => {
        const response = await api.put(`/events/${eventId}`, eventData);
        return response.data;
    },

    getAllMembers: async (eventDate = null) => {
        const params = eventDate ? { event_date: eventDate } : {};
        const response = await api.get('/users/members', { params });
        return response.data;
    },

    getMemberSpeakingHistory: async () => {
        const response = await api.get('/events/member-speaking-history');
        return response.data;
    },

    formatDate: (date) => {
        return new Date(date).toLocaleDateString('en-AU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    createVenue: async (venueData) => {
        const response = await api.post('/events/venues', venueData);
        return response.data;
    },

    getAllVenues: async () => {
        const response = await api.get('/events/venues');
        return response.data;
    },

    updateVenue: async (venueId, venueData) => {
        const response = await api.put(`/events/venues/${venueId}`, venueData);
        return response.data;
    },

    deleteVenue: async (venueId) => {
        const response = await api.delete(`/events/venues/${venueId}`);
        return response.data;
    },

    getEventDetails: async (eventId) => {
        const response = await api.get(`/events/${eventId}/details`);
        return response.data;
    },

    getEventAttendance: async (eventId) => {
        const response = await api.get(`/events/${eventId}/attendance`);
        return response.data;
    },

    updateEventSession: async (eventId, sessionType, sessionData) => {
        const response = await api.put(`/events/${eventId}/session/${sessionType}`, sessionData);
        return response.data;
    },

    getCheckInScores: async (userId) => {
        const response = await api.get(`/checkins/user/${userId}/scores`);
        return response.data;
    },

    getAvailableSpeakers: async (eventDate) => {
        const response = await api.get('/events/available-speakers', {
            params: { eventDate }
        });
        return response.data;
    }
};

export const emailService = {
    getTemplates: async () => {
        const response = await api.get('/email-templates');
        return response.data;
    },
    
    createTemplate: async (templateData) => {
        const response = await api.post('/email-templates', templateData);
        return response.data;
    },

    updateTemplate: async (id, templateData) => {
        const response = await api.put(`/email-templates/${id}`, templateData);
        return response.data;
    },

    deleteTemplate: async (id) => {
        const response = await api.delete(`/email-templates/${id}`);
        return response.data;
    },

    sendMemberNotifications: async (eventId, templateId) => {
        const response = await api.post('/email-templates/send-member', { 
            event_id: eventId, 
            template_id: templateId 
        });
        return response.data;
    },

    sendSpeakerNotifications: async (eventId, templateId, parsedTemplate) => {
        const response = await api.post('/email-templates/send-speaker', { 
            event_id: eventId, 
            template_id: templateId,
            parsed_template: parsedTemplate
        });
        return response.data;
    },

    queueNotifications: async (eventId, memberTemplateId, speakerTemplates, parsedTemplate) => {
        const response = await api.post('/email-templates/queue-notifications', {
            eventId,
            memberTemplateId,
            speakerTemplates,
            parsedTemplate
        });
        return response.data;
    },

    getNotificationStatus: async (eventId) => {
        const response = await api.get(`/email-templates/notification-status/${eventId}`);
        return response.data;
    },

    sendTestEmails: async (testEmail, eventId, memberTemplateId, speakerTemplates) => {
        const response = await api.post('/email-templates/send-test', {
            testEmail,
            eventId,
            memberTemplateId,
            speakerTemplates
        });
        return response.data;
    },

    sendVisitorInvitation: async (eventId, visitorId, invitedByMemberId) => {
        const response = await api.post('/email-templates/send-visitor-invitation', {
            eventId,
            visitorId,
            invitedByMemberId
        });
        return response.data;
    }
}; 